/**
 * Work that has to happen while the session is still valid, just before an
 * explicit sign-out revokes it.
 *
 * useAuth.signOut() revokes the refresh token globally and then reloads the
 * page, so anything that needs an authenticated call to undo per-user device
 * state — releasing this phone's push token (M8) is the first — cannot wait
 * for the SIGNED_OUT event: by then the RPC would be refused. Callers register
 * here instead.
 *
 * Every hook is best effort and the whole run is bounded: a slow network or a
 * failing hook must never hold the user inside an account they asked to leave.
 */
import { logger } from '@/lib/logger';

type BeforeSignOut = () => Promise<void>;

const hooks = new Set<BeforeSignOut>();

export type BoundedResult<T> =
  | { timedOut: false; value: T }
  | { timedOut: true };

/**
 * Wait for best-effort sign-out work without letting a dead network keep the
 * account open forever. The underlying promise is deliberately not cancelled:
 * a request already accepted by the browser may still finish after the local
 * session has been cleared.
 */
export async function settleWithin<T>(work: Promise<T>, timeoutMs: number): Promise<BoundedResult<T>> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<BoundedResult<T>>((resolve) => {
    timer = setTimeout(() => resolve({ timedOut: true }), timeoutMs);
  });
  try {
    return await Promise.race([
      work.then((value): BoundedResult<T> => ({ timedOut: false, value })),
      deadline,
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** Registers a hook; the returned function removes it. */
export function onBeforeSignOut(hook: BeforeSignOut): () => void {
  hooks.add(hook);
  return () => {
    hooks.delete(hook);
  };
}

/** Runs every registered hook in parallel, for at most `timeoutMs`. Never throws. */
export async function runBeforeSignOut(timeoutMs = 2500): Promise<void> {
  if (hooks.size === 0) return;
  const all = Promise.allSettled(
    [...hooks].map((hook) =>
      hook().catch((e) => {
        logger.warn('[sign-out] a before-sign-out hook failed', e);
      }),
    ),
  );
  await settleWithin(all, timeoutMs);
}
